import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Pause, Play, Clock, Timer as TimerIcon, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Timer = ({ sessaoId, tempoMaximoMinutos, isAdmin }) => {
  // NEW: Shared timer states
  const [timerStatus, setTimerStatus] = useState('NOT_STARTED');
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [duracaoTotalSegundos, setDuracaoTotalSegundos] = useState(null);
  
  // OLD: Keep existing states for "Tempo de Chamada" (unchanged)
  const [chamadaSegundos, setChamadaSegundos] = useState(0);
  const [inicioChamada, setInicioChamada] = useState(null);
  const [mostrarAviso5Min, setMostrarAviso5Min] = useState(false);
  
  const intervalRef = useRef(null);
  const syncIntervalRef = useRef(null);

  // NEW: Fetch shared timer state
  useEffect(() => {
    fetchSharedTimer();
    
    // Sync with server every 5 seconds
    syncIntervalRef.current = setInterval(fetchSharedTimer, 5000);
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (syncIntervalRef.current) clearInterval(syncIntervalRef.current);
    };
  }, [sessaoId]);

  // NEW: Local countdown animation (only for visual)
  useEffect(() => {
    if (timerStatus === 'RUNNING' && remainingSeconds > 0) {
      intervalRef.current = setInterval(() => {
        setRemainingSeconds(prev => {
          const novo = Math.max(0, prev - 1);
          return novo;
        });
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [timerStatus, remainingSeconds]);

  // Update chamada timer
  useEffect(() => {
    if (inicioChamada) {
      const updateChamadaTimer = () => {
        const agora = new Date();
        const inicio = new Date(inicioChamada);
        const diffMs = agora - inicio;
        const diffSec = Math.floor(diffMs / 1000);
        setChamadaSegundos(diffSec);

        // Check if 5 minutes remaining
        const tempoMaximoSeg = tempoMaximoMinutos * 60;
        const restantes = tempoMaximoSeg - diffSec;
        if (restantes === 300 && isAdmin && !mostrarAviso5Min) {
          setMostrarAviso5Min(true);
          toast.warning('Faltam 5 minutos para acabar a chamada. Não esqueça do checklist!');
        }
      };

      updateChamadaTimer();
      const chamadaInterval = setInterval(updateChamadaTimer, 1000);

      return () => clearInterval(chamadaInterval);
    }
  }, [inicioChamada, tempoMaximoMinutos, isAdmin, mostrarAviso5Min]);

  // NEW: Fetch shared timer state from backend
  const fetchSharedTimer = async () => {
    try {
      const response = await axios.get(`${API}/sessoes/${sessaoId}/timer`);
      const { timer_status, timer_remaining_seconds, timer_duracao_total_segundos } = response.data;
      
      setTimerStatus(timer_status);
      setRemainingSeconds(timer_remaining_seconds);
      setDuracaoTotalSegundos(timer_duracao_total_segundos);
      
      // Also fetch inicioChamada for "Tempo de Chamada" (OLD logic, unchanged)
      const sessaoResponse = await axios.get(`${API}/sessoes/${sessaoId}`);
      if (sessaoResponse.data.inicioChamada) {
        setInicioChamada(sessaoResponse.data.inicioChamada);
      }
    } catch (error) {
      console.error('Erro ao buscar timer:', error);
    }
  };

  // NEW: Start shared timer
  const handleStart = async () => {
    try {
      const response = await axios.post(`${API}/sessoes/${sessaoId}/timer/start`);
      const { timer_status, timer_remaining_seconds, timer_duracao_total_segundos } = response.data;
      
      setTimerStatus(timer_status);
      setRemainingSeconds(timer_remaining_seconds);
      setDuracaoTotalSegundos(timer_duracao_total_segundos);
      
      toast.success('Timer iniciado!');
    } catch (error) {
      console.error('Erro ao iniciar timer:', error);
      toast.error(error.response?.data?.detail || 'Erro ao iniciar timer');
    }
  };

  // NEW: Pause shared timer
  const handlePause = async () => {
    try {
      const response = await axios.post(`${API}/sessoes/${sessaoId}/timer/pause`);
      const { timer_status, timer_remaining_seconds } = response.data;
      
      setTimerStatus(timer_status);
      setRemainingSeconds(timer_remaining_seconds);
      
      toast.success('Timer pausado!');
    } catch (error) {
      console.error('Erro ao pausar timer:', error);
      toast.error(error.response?.data?.detail || 'Erro ao pausar timer');
    }
  };

  // NEW: Resume shared timer
  const handleResume = async () => {
    try {
      const response = await axios.post(`${API}/sessoes/${sessaoId}/timer/resume`);
      const { timer_status, timer_remaining_seconds } = response.data;
      
      setTimerStatus(timer_status);
      setRemainingSeconds(timer_remaining_seconds);
      
      toast.success('Timer retomado!');
    } catch (error) {
      console.error('Erro ao retomar timer:', error);
      toast.error(error.response?.data?.detail || 'Erro ao retomar timer');
    }
  };

  const formatTime = (segundos) => {
    const mins = Math.floor(segundos / 60);
    const secs = segundos % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // NEW: Color based on remaining seconds
  const getTimerColor = () => {
    if (!remainingSeconds || remainingSeconds <= 0) return 'text-red-600';
    if (remainingSeconds <= 60) return 'text-red-600';
    if (remainingSeconds <= 180) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getChamadaColor = () => {
    const tempoMaximoSeg = tempoMaximoMinutos * 60;
    const restantes = tempoMaximoSeg - chamadaSegundos;
    if (restantes <= 300) return 'text-red-600'; // 5 min ou menos
    if (restantes <= 600) return 'text-yellow-600'; // 10 min ou menos
    return 'text-blue-600';
  };

  return (
    <div className="space-y-4">
      {/* NEW: Shared Timer (Cronômetro Compartilhado) */}
      {duracaoTotalSegundos ? (
        <Card className="bg-white border-gray-200">
          <CardHeader>
            <CardTitle className="text-[#191919] flex items-center gap-2 text-lg">
              <TimerIcon className="w-5 h-5 text-[#CF0D1D]" />
              Cronômetro Compartilhado
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className={`text-4xl font-bold font-mono ${getTimerColor()}`}>
                  {remainingSeconds !== null ? formatTime(remainingSeconds) : '--:--'}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {timerStatus === 'NOT_STARTED' && 'Aguardando início'}
                  {timerStatus === 'RUNNING' && 'Em andamento'}
                  {timerStatus === 'PAUSED' && 'Pausado'}
                  {timerStatus === 'FINISHED' && 'Finalizado'}
                </p>
              </div>
              {timerStatus === 'NOT_STARTED' && (
                <Button
                  onClick={handleStart}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="start-button"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Iniciar
                </Button>
              )}
              {timerStatus === 'RUNNING' && (
                <Button
                  onClick={handlePause}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="pause-button"
                >
                  <Pause className="w-4 h-4 mr-2" />
                  Pausar
                </Button>
              )}
              {timerStatus === 'PAUSED' && (
                <Button
                  onClick={handleResume}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="resume-button"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Continuar
                </Button>
              )}
              {timerStatus === 'FINISHED' && (
                <Button
                  disabled
                  className="bg-gray-400 cursor-not-allowed"
                >
                  Finalizado
                </Button>
              )}
            </div>
            {remainingSeconds !== null && remainingSeconds <= 60 && remainingSeconds > 0 && (
              <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <p className="text-xs text-red-800 font-semibold">Último minuto!</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* Timer de Chamada */}
      {inicioChamada && (
        <Card className="bg-white border-gray-200">
          <CardHeader>
            <CardTitle className="text-[#191919] flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-blue-600" />
              Tempo de Chamada
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className={`text-3xl font-bold font-mono ${getChamadaColor()}`}>
                {formatTime(chamadaSegundos)}
              </div>
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Tempo máximo:</span>
                <span className="font-semibold">{formatTime(tempoMaximoMinutos * 60)}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${getChamadaColor().replace('text', 'bg')}`}
                  style={{ width: `${Math.min(100, (chamadaSegundos / (tempoMaximoMinutos * 60)) * 100)}%` }}
                ></div>
              </div>
              {isAdmin && (tempoMaximoMinutos * 60 - chamadaSegundos) <= 300 && (tempoMaximoMinutos * 60 - chamadaSegundos) > 0 && (
                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                  <p className="text-xs text-yellow-800 font-semibold">
                    Faltam {Math.floor((tempoMaximoMinutos * 60 - chamadaSegundos) / 60)} minutos. Não esqueça do checklist!
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Timer;